import { html, LitElement } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASK_TARGET_FORM } from '../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../ui/gzForm/formGroup/form-group';
import { InputWidthValues } from '../../../../../../../../../ui/gzInput';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../../utils/htmlElement.utils';
import {
  AntimalwareOnDemandExclusionProfile,
  AntimalwareOnDemandExclusionTask,
  AntimalwareOnDemandScanTarget,
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskScanType,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../ui/gzRadio';
import { FontSizes, FontWeights } from '../../../../../../../../../types/common/types';
import { GzAntimalwareOnDemandExclusionsTableWebComponent } from '../gridComponents/exclusions/GzAntimalwareOnDemandExclusionsTableWebComponent';
import { GzAntimalwareOnDemandScanTargetTableWebComponent } from '../gridComponents/scanTarget/GzAntimalwareOnDemandScanTargetTableWebComponent';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND_SCAN_TASK_TARGET_FORM)
export class GzAntimalwareOnDemandScanTaskTargetFormWebComponent extends LitElement {
  // target section
  @query('#tasks-target-section')
  private targetsSection: HTMLElement;
  @query('#tasks-targets-credentials-section')
  private credentialsSection: HTMLElement;
  @query('#tasks-scan-settings-credentials-user-field')
  private scanSettingsCredentialsUserField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#tasks-scan-settings-credentials-password-field')
  private scanSettingsCredentialsPasswordField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#tasks-exclusions-profile-field')
  private exclusionsProfileField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#tasks-exclusions-task-list-field')
  private exclusionsTaskListField: GzFormFieldWebComponent<
    GzAntimalwareOnDemandExclusionsTableWebComponent,
    AntimalwareOnDemandExclusionTask[]
  >;
  @query('#tasks-scan-target-field')
  private scanTargetField: GzFormFieldWebComponent<
    GzAntimalwareOnDemandScanTargetTableWebComponent,
    AntimalwareOnDemandScanTarget[]
  >;

  public formGroup: FormGroup;
  private value: AntimalwareOnDemandScanTask | undefined;
  private scanType = AntimalwareOnDemandScanTaskScanType.QUICK;

  /**
   * Create form group and subscribe to valid.
   * @param changedProperties
   */
  protected firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.createFormGroup();
  }

  static get styles() {
    return styles;
  }

  async createFormGroup() {
    this.scanSettingsCredentialsUserField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.scanSettingsCredentialsPasswordField.Validators = [RequiredValidator(translateFn('Required field'))];

    this.scanTargetField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.scanTargetField.touched = true;

    this.formGroup = FormGroup.build({
      scanSettings: {
        networkCredentials: {
          user: this.scanSettingsCredentialsUserField,
          password: this.scanSettingsCredentialsPasswordField,
        },
      },
      scanTarget: this.scanTargetField,
      exclusions: {
        profile: this.exclusionsProfileField,
        taskList: this.exclusionsTaskListField,
      },
    });
  }

  public async setFormGroupValue(scanType, value) {
    await this.firstUpdated;

    this.value = value;
    this.scanType = scanType;
    this.setDefaultViewForScanType();

    if (!this.value) {
      return;
    }

    this.formGroup.value = {
      scanSettings: {
        networkCredentials: {
          user: this.value.scanSettings.networkCredentials?.user,
          password: this.value.scanSettings.networkCredentials?.password,
        },
      },
      scanTarget: this.value.scanTarget,
      exclusions: this.value.exclusions,
    };
  }

  private setDefaultViewForScanType() {
    const isNetworkType = this.scanType === AntimalwareOnDemandScanTaskScanType.NETWORK;

    toggleVisibility(this.credentialsSection, isNetworkType);
    this.scanSettingsCredentialsUserField.disabled = !isNetworkType;
    this.scanSettingsCredentialsPasswordField.disabled = !isNetworkType;

    if (!isNetworkType) {
      this.formGroup.removeFormChild(['scanSettings']);
    }
  }

  /**
   * Render specific content.
   */
  protected render() {
    return html`
      <!-- Targets - Scan target section -->
      <div id="tasks-targets-scan-target-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('Scan target')}</gz-title
        >

        <gz-form-field id="tasks-scan-target-field">
          <gz-policy-panel-antimalware-on-demand-scan-target-table
            class="hideButtonsOutsideGrid"
            gz-form-input
          ></gz-policy-panel-antimalware-on-demand-scan-target-table>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>

      <!-- Targets - Define credentials for network files scanning section -->
      <div id="tasks-targets-credentials-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-3"
          >${translateFn('Define credentials for network files scanning')}</gz-title
        >

        <gz-form-field
          id="tasks-scan-settings-credentials-user-field"
          class="mt-2"
          label="${translateFn('Username')}"
          required
        >
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Enter username')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="tasks-scan-settings-credentials-password-field" label="${translateFn('Password')}" required>
          <gz-input
            gz-form-input
            type="password"
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Enter password')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>

      <!-- Targets - Exclusions section -->
      <div id="tasks-targets-exclusions-section">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="subtitle mt-4"
          >${translateFn('Exclusions')}</gz-title
        >

        <gz-form-field id="tasks-exclusions-profile-field" class="mt-2">
          <gz-radio-group
            gz-form-input
            class="d-flex flex-column"
            @radioSelectionChanged=${e => {
              toggleDisabled(
                this.exclusionsTaskListField,
                e.detail.selectedValue === AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION
              );
              this.exclusionsTaskListField.Validators =
                e.detail.selectedValue === AntimalwareOnDemandExclusionProfile.DEFINED_EXCLUSIONS
                  ? [RequiredValidator(translateFn('Required field'))]
                  : [];
              this.exclusionsTaskListField.validate(true);
            }}
          >
            <div class="radio pb-2">
              <gz-radio
                label="${translateFn('Use the exclusions defined in Antimalware > Exclusions')}"
                .value=${AntimalwareOnDemandExclusionProfile.GLOBAL_EXCLUSION}
              ></gz-radio>
            </div>

            <div class="radio">
              <gz-radio
                label="${translateFn('Define custom exclusions for this scan')}"
                .value=${AntimalwareOnDemandExclusionProfile.DEFINED_EXCLUSIONS}
              ></gz-radio>
            </div>
          </gz-radio-group>
        </gz-form-field>

        <gz-form-field id="tasks-exclusions-task-list-field">
          <gz-policy-panel-antimalware-on-demand-exclusions-table
            class="hideButtonsOutsideGrid"
            gz-form-input
          ></gz-policy-panel-antimalware-on-demand-exclusions-table>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }
}
